import styled from 'styled-components';
import React, { ChangeEvent, FC, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation, TFunction } from 'react-i18next';
import { push as Push } from 'redux-first-history';
import { isEmpty } from 'lodash-es';
import { CustomTable, Pagination, Paper, SimplePopoverPlainOption, EmptyState, MobileTable } from '@dev/ee-components';
import { JourneyStatus, JourneySummary } from '@dev/ee-api/ee-api-models';
import { safelyEncodeBase64Url } from '@dev/ee-tools';

import {
    GetAllAutomationsStart,
    getAutomationStore,
    PickAutomations,
    UnpickAllAutomations,
} from '@/store/automations/actions';
import useMobile from '@/services/hooks/useMobile';
import { DropdownOptions } from '@/store/automations/types';
import { AutomationPaths } from '@/services/dictionaries/Paths';
import { useTableRows } from './hooks/useTableRows/index';
import { useTableHeaders } from './hooks/useTableHeaders/index';
import { TableFiltersComponent } from './components/TableFilters/index';
import { useMobileTableRows } from './hooks/useMobileTableRows/index';
import { MobileTableFilters } from './components/MobileTableFilters';
import { useRequestParams } from '../hooks/useRequestParams';
import { useDropdownActions } from './hooks/useDropdownActions';

const TableContainer = styled.div`
    margin-top: 32px;
`;
const MobileTableContainer = styled.div`
    margin-top: 24px;

    & > div {
        padding-bottom: 2px;
    }
`;

const CustomPagination: FC = (): JSX.Element => {
    const automations = useSelector(getAutomationStore);
    const dispatch = useDispatch();
    const isMobile = useMobile();
    const defaultStatuses = [JourneyStatus.Inactive];
    const params = useRequestParams(defaultStatuses);

    const totalCount = automations?.loading ? 0 : automations?.statusCounts?.total;

    const changePage = (page: number, perPage: number): void => {
        const offset = page * perPage - perPage;
        dispatch(GetAllAutomationsStart({ ...params, limit: perPage, offset }));
    };

    const activePage = (params.offset + params.limit) / params.limit;

    return isEmpty(automations?.collection) && !automations?.loading ? null : isMobile ? (
        <Paper padding={{ top: 24, bottom: 24 }}>
            <Pagination
                dataLength={totalCount}
                perPage={params.limit}
                activePage={activePage}
                handlePaginationChange={changePage}
                disableBorderTop
            />
        </Paper>
    ) : (
        <Pagination
            dataLength={totalCount}
            perPage={params.limit}
            activePage={activePage}
            handlePaginationChange={changePage}
        />
    );
};

const columnWidth: Record<string, string> = {
    automationName: '40%',
    opened: '10%',
    clicked: '10%',
    status: '130px',
    viewreport: '130px',
};

const getDropdownTableOptions = (t: TFunction, automation?: JourneySummary): SimplePopoverPlainOption[] => [
    ...(automation?.Status === JourneyStatus.Active
        ? [
              {
                  label: t('pause'),
                  value: DropdownOptions.Pause,
              },
          ]
        : []),
    ...(automation?.Status === JourneyStatus.Paused
        ? [
              {
                  label: t('reactivate'),
                  value: DropdownOptions.Reactivate,
              },
          ]
        : []),
    {
        label: t('delete'),
        color: '#ff0000',
        value: DropdownOptions.Delete,
    },
];

const AutomationsTable: FC = (): JSX.Element => {
    const automations = useSelector(getAutomationStore);
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const pickAutomation = (event: ChangeEvent<unknown>, pickedAutomations: string[]) => {
        event?.stopPropagation?.();
        event?.preventDefault?.();

        dispatch(PickAutomations(pickedAutomations));
    };
    const headers = useTableHeaders();
    const rows = useTableRows();

    return (
        <TableContainer>
            <TableFiltersComponent />
            <CustomTable
                headers={headers}
                rows={rows}
                isEmpty={isEmpty(automations?.collection) && !automations?.loading}
                loading={automations?.loading}
                stickyHeader
                rowPadding={48}
                rowHeight={60}
                columnsWidth={columnWidth}
                emptyStateProps={{ text: t('emptystate') }}
                disableFirstRowBorderTop
                onSelectRow={pickAutomation}
                selectedRows={automations.checkedAutomations}
                isHeaderCheckboxEnabled
                disableRowClick
            />
        </TableContainer>
    );
};

const MobileAutomationsTable: FC = (): JSX.Element => {
    const automations = useSelector(getAutomationStore);
    const { t } = useTranslation();
    const header = [t('status'), t('opened'), t('clicked')];
    const rows = useMobileTableRows();
    const dispatch = useDispatch();
    const dropdownActions = useDropdownActions();
    const options = [
        {
            label: t('delete'),
            color: '#ff0000',
            value: DropdownOptions.Delete,
        },
    ];

    if (isEmpty(automations?.collection) && !automations?.loading) {
        return (
            <Paper>
                <EmptyState text={t('emptystate')} />
            </Paper>
        );
    }

    return (
        <MobileTableContainer>
            <MobileTable
                headers={header}
                rows={rows}
                dropdownData={options}
                dropdownDataFilter={(opt, returnOnItemClick) => getDropdownTableOptions(t, returnOnItemClick)}
                buttonText={t('viewreports')}
                onClickButton={(_, singleAutomation): void => {
                    dispatch(
                        Push(
                            AutomationPaths.AutomationReport.replace(
                                ':name',
                                safelyEncodeBase64Url(singleAutomation.returnOnItemClick.Name)
                            )
                        )
                    );
                }}
                onItemClick={(e, opt, automation): void =>
                    dropdownActions(opt as { label: string; value: DropdownOptions }, automation)
                }
            />
        </MobileTableContainer>
    );
};

const AutomationsList: FC = (): JSX.Element => {
    const dispatch = useDispatch();
    const isMobile = useMobile();

    const defaultStatuses = [JourneyStatus.Invalid, JourneyStatus.Active, JourneyStatus.Paused];
    const requestParams = useRequestParams(defaultStatuses);

    useEffect(() => {
        dispatch(GetAllAutomationsStart(requestParams));
        return () => {
            dispatch(UnpickAllAutomations());
        };
    }, []);

    return isMobile ? (
        <>
            <MobileTableFilters />
            <MobileAutomationsTable />
            <CustomPagination />
        </>
    ) : (
        <Paper margin={{ top: 32 }}>
            <AutomationsTable />
            <CustomPagination />
        </Paper>
    );
};

export default AutomationsList;
